import React, { useState, useEffect, useMemo } from 'react';
import {
  Wallet,
  Ticket,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Gift,
  Crown,
  Loader2,
  RotateCcw,
  Plus,
  Minus,
  Sparkles,
  Handbag,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { TENANT_ID } from "../../../tenant";

import GiftModal from './GiftModal';

// --- 1. ヘルパー ---
const normalizeType = (t) => {
  const s = String(t || '').toLowerCase();
  if (s === 'goods') return 'goods';
  if (s === 'plan') return 'plan';
  return 'ticket';
};

const getRemaining = (e) => {
  const total = parseInt(e.quantity_total || 0, 10);
  const used = parseInt(e.quantity_used || 0, 10);
  const pending = parseInt(e.pending_quantity || 0, 10);
  return total - used - pending;
};

// --- 2. 枚数選択モーダル ---
const QuantitySelectModal = ({ isOpen, onClose, max, onConfirm, itemName }) => {
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (isOpen) setCount(1);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/60 z-[20000] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in text-gray-900">
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-slide-up text-center border border-gray-100">
        <h3 className="text-lg font-black mb-1">1人の友だちに贈る枚数を選択</h3>
        <p className="text-xs text-gray-500 mb-6 font-bold">{itemName}</p>

        <div className="flex items-center justify-center gap-8 mb-8">
          <button
            onClick={() => setCount(Math.max(1, count - 1))}
            className="w-12 h-12 rounded-full border-2 border-gray-100 flex items-center justify-center active:bg-gray-50 transition-colors"
          >
            <Minus size={20} />
          </button>

          <span className="text-4xl font-black w-12 font-mono">{count}</span>

          <button
            onClick={() => setCount(Math.min(max, count + 1))}
            className="w-12 h-12 rounded-full border-2 border-gray-100 flex items-center justify-center active:bg-gray-50 transition-colors"
          >
            <Plus size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => onConfirm(count)}
            className="w-full bg-gray-900 text-white font-black py-4 rounded-3xl shadow-lg active:scale-95 transition-all"
          >
            {count}枚をギフトにする
          </button>
          <button onClick={onClose} className="w-full text-gray-500 font-bold py-2 text-sm">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
};

// --- 3. メイン ---
const PossessionsCard = ({
  user,
  activeSubscriptions = [],
  productMap = {},
  entitlements = [],
  pendingGifts = [],
  isOpen,
  onToggle,
  onOpenShop,

  // ✅ 追加：GiftReceivePage -> Dashboard -> PossessionsCard で渡す
  highlightGiftToken = null,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedGiftItem, setSelectedGiftItem] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // ✅ ギフトアイコンを「普段は隠す」ためのstate
  const [showGiftTopIcon, setShowGiftTopIcon] = useState(false);

  // ✅ Step6：ハイライトは「一時的」にしたいのでローカルstateに落とす
  const [localHighlightToken, setLocalHighlightToken] = useState(null);

  // ✅ Step6：token を受け取ったら一定時間だけ保持して自動で消す
  useEffect(() => {
    // ✅ 親から token が消えたら、ローカルも即消す（保険）
    if (!highlightGiftToken) {
      setLocalHighlightToken(null);
      return;
    }

    setLocalHighlightToken(String(highlightGiftToken));

    const t = setTimeout(() => {
      setLocalHighlightToken(null);
    }, 12000);

    return () => clearTimeout(t);
  }, [highlightGiftToken]);

  const summarizedData = useMemo(() => {
    // ✅ 予約ticket（予約由来 entitlement）を除外する判定
    const isReservationEntitlement = (e) => {
      if (!e) return false;

      // 予約ID系が入っていれば予約由来とみなす（実装差分を吸収）
      const rid =
        e.reservation_id ||
        e.reservationId ||
        e.booking_id ||
        e.bookingId ||
        e?.metadata?.reservation_id ||
        e?.metadata?.reservationId ||
        e?.metadata?.booking_id ||
        e?.metadata?.bookingId ||
        null;

      if (rid) return true;

      // source に reservation を含める運用を吸収
      const src = String(e.source || '').toLowerCase();
      if (src.includes('reservation')) return true;

      return false;
    };

    // ✅ ここが修正点：予約ticketを allDocs から落とす
    const allDocsRaw = (Array.isArray(entitlements) ? entitlements : [])
      .filter((e) => !isReservationEntitlement(e))
      .map((e) => ({ ...e }));

    // ✅ plan は plan_active だけを正本として表示（2重表示対策）
    const allDocs = allDocsRaw.filter((e) => {
      const type = normalizeType(e?.product_type);
      if (type !== 'plan') return true;
      return String(e?.id || e?.docId || e?.entitlement_id || '') === 'plan_active';
    });

    const groups = {};

    // ✅ Step5：このtoken由来かどうか（entitlement単体で判定）
    const isEntHighlighted = (e) => {
      if (!localHighlightToken) return false;
      return String(e?.gift_token || '') === String(localHighlightToken);
    };

    allDocs.forEach((e) => {
      if (e.is_active !== true) return;

      const type = normalizeType(e.product_type);
      const pId = e.product_id || 'unknown';
      const title = e.product_name || productMap[pId]?.name || 'アイテム';

      // plan は数量概念が薄いので「存在」で表示
      if (type === 'plan') {
        const key = `plan_${pId}`;
        if (!groups[key]) {
          groups[key] = {
            product_id: pId,
            title,
            type: 'plan',
            totalQty: 1,
            isPending: false,
            docs: [e],
            // ✅ plan は対象外にするなら false 固定でもOK
            isHighlighted: isEntHighlighted(e),
          };
        } else {
          groups[key].docs.push(e);
          groups[key].isHighlighted = groups[key].isHighlighted || isEntHighlighted(e);
        }
        return;
      }

      const remaining = getRemaining(e);
      const pendingQty = parseInt(e.pending_quantity || 0, 10);

      // pending はお届け中へ（entitlements内の pending_quantity）
      if (pendingQty > 0) {
        const key = `pending_${pId}`;
        if (!groups[key]) {
          groups[key] = {
            product_id: pId,
            title,
            type,
            totalQty: 0,
            isPending: true,
            docs: [],
            isHighlighted: false,
          };
        }
        groups[key].totalQty += pendingQty;
        groups[key].docs.push(e);
      }

      // 手元残り
      if (remaining > 0) {
        const key = pId;
        if (!groups[key]) {
          groups[key] = {
            product_id: pId,
            title,
            type,
            totalQty: 0,
            isPending: false,
            docs: [],
            // ✅ Step5：このグループに highlight 対象entitlementが1つでも混じってたら true
            isHighlighted: false,
          };
        }
        groups[key].totalQty += remaining;
        groups[key].docs.push(e);
        groups[key].isHighlighted = groups[key].isHighlighted || isEntHighlighted(e);
      }
    });

    const summarized = Object.values(groups);
    return {
      tickets: summarized.filter((i) => i.type === 'ticket' && !i.isPending),
      goods: summarized.filter((i) => i.type === 'goods' && !i.isPending),
      plans: summarized.filter((i) => i.type === 'plan'),
      pending: summarized.filter((i) => i.isPending),
    };
  }, [entitlements, productMap, localHighlightToken]);

  const hasAnyItems = useMemo(() => {
    const a = pendingGifts.length || 0;
    const b = summarizedData.pending?.length || 0;
    const c = summarizedData.tickets?.length || 0;
    const d = summarizedData.goods?.length || 0;
    const e = summarizedData.plans?.length || 0;
    return a + b + c + d + e > 0;
  }, [summarizedData, pendingGifts]);

  const giftables = useMemo(() => {
    return [...(summarizedData.tickets || []), ...(summarizedData.goods || [])].filter(
      (x) => (x?.totalQty || 0) > 0
    );
  }, [summarizedData]);

  const giftableCount = giftables.length;

  const giftUi = useMemo(() => {
    const pendingCount = pendingGifts.length;
    const hasPending = pendingCount > 0;
    const hasGiftable = giftableCount > 0;

    const disabled = isProcessing || (!hasGiftable && !hasPending);

    const toneClass = disabled
      ? 'opacity-40 cursor-not-allowed text-gray-500'
      : hasGiftable
      ? 'text-gray-700 hover:text-gray-900'
      : 'text-gray-900 hover:text-gray-900';

    const title = isProcessing
      ? '処理中です'
      : !hasGiftable && hasPending
      ? 'お届け中を確認'
      : !hasGiftable
      ? 'ギフトできるアイテムがありません'
      : giftableCount === 1
      ? 'ギフトする'
      : '一覧を開く';

    return {
      pendingCount,
      hasPending,
      hasGiftable,
      disabled,
      toneClass,
      title,
    };
  }, [giftableCount, isProcessing, pendingGifts]);

  // ✅ 表示条件：普段は非表示、ただし「操作を始めた」or「pendingバッジが必要」なら表示
  const shouldShowGiftTopIcon = useMemo(() => {
    // ★仕様：相手が受け取って pending が無くなったら「閉じた時」に消したい
    // → pending がある間だけ “開いている時” は表示OK（バッジ必要）
    // → それ以外は showGiftTopIcon（=手動で出した）に依存
    return showGiftTopIcon || (isOpen && giftUi.hasPending);
  }, [showGiftTopIcon, giftUi.hasPending, isOpen]);

  // ✅ 追加：相手が受け取って pending が消えた後、アコーディオンを閉じたら右上ギフトアイコンを消す
  useEffect(() => {
    if (!isOpen && !giftUi.hasPending) {
      setShowGiftTopIcon(false);
    }
  }, [isOpen, giftUi.hasPending]);

  const oneLineSummary = useMemo(() => {
    const list = [
      ...(pendingGifts || []),
      ...(summarizedData.pending || []),
      ...(summarizedData.tickets || []),
      ...(summarizedData.goods || []),
      ...(summarizedData.plans || []),
    ];

    if (list.length === 0) {
      return { icon: <Sparkles size={18} className="text-gray-300" />, text: '' };
    }

    const first = list[0];
    const more = list.length - 1;

    const title = String(first?.title || 'アイテム');
    const trimmed = title.length > 20 ? `${title.slice(0, 20)}…` : title;
    const text = more > 0 ? `${trimmed} 他${more}件` : trimmed;

    if ((pendingGifts?.length || 0) > 0) return { icon: <Gift size={18} className="text-amber-500" />, text };
    if (summarizedData.pending?.length > 0) return { icon: <Gift size={18} className="text-gray-900" />, text };
    if (summarizedData.tickets?.length > 0) return { icon: <Ticket size={18} className="text-gray-900" />, text };
    if (summarizedData.goods?.length > 0) return { icon: <Handbag size={18} className="text-amber-500" />, text };
    return { icon: <Crown size={18} className="text-gray-900" />, text };
  }, [summarizedData, pendingGifts]);

  const summaryText = useMemo(() => {
    const hasGiftPending = (pendingGifts?.length || 0) > 0;

    if (isOpen) return '一覧を表示中';

    if (hasGiftPending) {
      return `Giftお届け中… ${pendingGifts.length}件`;
    }

    return oneLineSummary.text;
  }, [isOpen, oneLineSummary.text, pendingGifts]);

  // ===== ギフト発行 =====
  const handleIssueGift = async (groupItem, sendQty) => {
    setIsProcessing(true);

    try {
      const createFn = httpsCallable(functions, 'createGiftToken');

      const first = Array.isArray(groupItem?.docs) ? groupItem.docs[0] : null;
      const entitlementId = String(first?.docId || first?.id || first?.entitlement_id || '').trim();
      if (!entitlementId) throw new Error('entitlementId missing');

      // ✅ まずモーダルを即表示（リンク未確定のプレースホルダ）
      setSelectedGiftItem({
        issuing: true,
        token: null,
        url: '',
        product_name: groupItem?.title || 'ギフト',
        quantity: Number(sendQty || 1),
        product_type: groupItem?.type || 'ticket',
      });

      const res = await createFn({
        tenantId: TENANT_ID,
        entitlementId,
        quantity: sendQty,
        product_name: groupItem?.title || null,
      });

      const data = res?.data || {};
      const token = String(data?.token || '').trim();
      if (!token) throw new Error('token missing');

      setSelectedGiftItem({
        issuing: false,
        token,

        quantity: Number(data?.quantity || sendQty),
        product_name: data?.product_name || groupItem?.title || 'ギフト',
        product_type: data?.product_type || groupItem?.type || 'ticket',
        url: data?.url || `${window.location.origin}/gift/receive/${token}`,
        
      });

      //setQtyModal({ open: false, item: null });
    } catch (e) {
      console.error(e);
      // ✅ モーダル内にエラーを出す（体感が良い）
      setSelectedGiftItem((prev) => ({
        ...(prev || {}),
        issuing: false,
        issueErr: `発行に失敗しました: ${e?.message || String(e)}`,
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  // ===== ギフトキャンセル =====
  const handleCancelGift = async (token) => {
    if (!token) return;

    setCancellingId(token);

    try {
      const fn = httpsCallable(functions, 'cancelGiftToken');
      await fn({ tenantId: TENANT_ID, token });

      setShowGiftTopIcon(false);
    } catch (err) {
      console.error('Cancel Gift Error:', err);
      // 必要ならここも alert をやめてカード内表示にする
      // alert(`キャンセルに失敗しました: ${err?.message || String(err)}`);
    } finally {
      setCancellingId(null);
    }
  };

  const handlePressGiftIcon = () => {
    if (isProcessing) return;

    // ギフト可能が無いなら、お届け中だけ確認（従来）
    if (!giftUi.hasGiftable) {
      if (giftUi.hasPending) {
        if (!isOpen) onToggle?.();
      }
      return;
    }

    // 送れるものが1種類だけなら、それを“常に1枚”発行
    if (giftableCount === 1) {
      const item = giftables[0];
      if (!item) return;

      setShowGiftTopIcon(true);
      handleIssueGift(item, 1); // ✅ 常に1
      return;
    }

    // 複数種類あるなら一覧を開く（従来）
    if (!isOpen) onToggle?.();
  };
  // ===== セクション描画 =====
  const renderSection = (title, items, icon, colorClass, isPendingSection = false) => {
    if (items.length === 0) return null;

    return (
      <div className="space-y-2 mb-6 text-left">
<h4
  className={`text-[10px] font-black uppercase tracking-widest pl-1 flex items-center gap-1.5 ${
    title === 'Plans' ? 'mt-4' : ''
  } ${isPendingSection ? 'text-amber-500' : 'text-gray-500'}`}
>
 {title}
</h4>

        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={isPendingSection ? item.token || item.id : item.product_id}
              className={`bg-white p-4 rounded-3xl border shadow-sm transition-all ${
                isPendingSection
                  ? 'bg-amber-50/50 border-amber-100'
                  : item?.isHighlighted
                  ? 'border-gray-200 bg-gray-50/50 ring-2 ring-gray-200 animate-pulse'
                  : 'border-gray-100'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <span className={isPendingSection ? 'text-amber-500' : colorClass}>{icon}</span>
                  <span className="text-sm font-black text-gray-900 leading-tight">{item.title}</span>
                </div>

                <div className="bg-gray-50 text-gray-600 text-[10px] font-black px-2 py-1 rounded-xl border border-gray-100 font-mono">
                  x {item.totalQty}
                </div>
              </div>

              <div className="flex justify-end border-t border-gray-50 pt-3 mt-1">
                {isPendingSection ? (
                  <button
                    onClick={() => handleCancelGift(item.token)}
                    className="flex items-center gap-1.5 border border-amber-500 text-amber-600 px-3 py-1.5 rounded-xl text-[10px] font-bold active:scale-95 bg-white shadow-sm transition-all"
                    disabled={isProcessing}
                  >
                    {cancellingId === item.token ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RotateCcw size={12} />
                    )}{' '}
                    キャンセル
                  </button>
                ) : (
                  item.type !== 'plan' && (
                    <button
                      onClick={() => {
                        // ✅ 「体験ギフトを贈る」を押したら、右上ギフトアイコンを表示
                        setShowGiftTopIcon(true);

                        // ✅ 常に1枚だけ送る
                        handleIssueGift(item, 1);
                      }}
                      disabled={isProcessing}
                      className="flex items-center gap-1.5 border border-gray-900 text-gray-900 px-3 py-1.5 rounded-xl text-xs font-bold active:scale-95 bg-white shadow-sm transition-all"
                    >
                      <Gift size={14} /> 体験ギフトを贈る
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const rightIconButtonClass =
    'w-8 h-8 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center active:scale-[0.98] transition-all';
  const closeButtonClass =
    'w-full mt-8 py-3.5 rounded-3xl bg-white border-1 border-gray-400 text-gray-700 ' +
    'text-m font-black flex items-center justify-center gap-2 ' +
    'hover:bg-gray-100 active:scale-[0.99] transition-all';

  return (
    <div className="bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans">
      <div className="flex items-center gap-1 px-0.5 text-gray-900">
        <div className="w-6 h-6 flex items-center justify-center">
          <Wallet size={15} />
        </div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">使えるもの</p>
      </div>

      <button
        onClick={onOpenShop}
        className="w-full py-3.5 rounded-3xl flex items-center justify-center gap-2 font-black text-base active:scale-[0.97] transition-all bg-accent text-white shadow-sm"
      >
        <ShoppingBag size={20} /> 購入する
      </button>

      {/* ✅ ヘッダー：右端は「下矢印」固定、ギフトアイコンは条件付きで表示 */}
      <div className="flex items-center gap-3">
        <div
          onClick={onToggle}
          className={`flex-1 min-w-0 bg-gray-50/30 rounded-xl px-5 py-3 border border-gray-100 cursor-pointer flex items-center justify-between transition-colors active:bg-gray-100 ${
            isOpen ? 'opacity-60' : ''
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">

            <div
              className={`text-[13px] font-black tracking-tighter truncate ${
                !isOpen && (pendingGifts?.length || 0) > 0
                  ? 'text-amber-500'
                  : 'text-gray-800'
              }`}
            >
              {summaryText}
            </div>
          </div>

          {/* ✅ 右端は下矢印のみ（reservationscard風） */}
          <ChevronDown
            size={20}
            className={`text-gray-900 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {/* ✅ ギフト：普段は非表示。ギフト操作を開始 or pending 確認(開いている間)で表示 */}

      </div>

      {isOpen && (
        <div className="pt-2 animate-fade-in border-t border-gray-50">
          {!hasAnyItems ? (
            <div className="bg-gray-50 rounded-3xl border border-gray-100 p-5 text-center">
              <div className="text-[12px] font-black text-gray-800 mb-1">購入してみよう！</div>
              <div className="text-[11px] text-gray-500 font-bold leading-relaxed mb-1">
                所持品の表示と、ギフトを送るページです。
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {renderSection('Giftお届け中...', pendingGifts, <Gift size={16} />, 'text-amber-500', true)}
              {renderSection('Tickets', summarizedData.tickets, <Ticket size={16} />, 'text-gray-900')}
              {renderSection('Goods', summarizedData.goods, <Handbag size={16} />, 'text-amber-500')}
              {renderSection('Plans', summarizedData.plans, <Crown size={16} />, 'text-gray-900')}
            </div>
          )}

          <button onClick={onToggle} className={closeButtonClass}>
            <ChevronUp size={20} className="text-gray-900" /> 閉じる
          </button>
        </div>
      )}

      <GiftModal
        isOpen={!!selectedGiftItem}
        onClose={() => {
          setSelectedGiftItem(null);
          // ✅ pending が無いなら、ギフト操作終了で右上アイコンを隠す（好みで消してOK）
          if (!giftUi.hasPending) setShowGiftTopIcon(false);
        }}
        ticket={selectedGiftItem}
      />
    </div>
  );
};

export default PossessionsCard;
// テナント化済み　createGiftToken / cancelGiftToken に tenantId をクライアントから渡すのをやめる方法を検討